import { differenceInMilliseconds, differenceInSeconds } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import {
  affectsEarnings,
  getCursedEarnings,
  getMilestoneTimes,
  isJailedAt,
  Siphon,
} from "./curses";
import {
  BASE_COINS_PER_HOUR,
  CoinLineItem,
  getSessionBonuses,
  getStreakMultiplier,
  getStreakWeeks,
  isThursdayCheckIn,
  sumCoin,
} from "./economy";
import { calculatePowerupEarnings } from "./powerups";
import { Session } from "./types";

export function getZonedDate() {
  return toZonedTime(new Date(), "Europe/London");
}

/** Pubs are always open: only being in range of one matters, unless an admin has disabled it. */
export function isVenueOpen(venue: { enabled?: boolean }) {
  return venue.enabled !== false;
}

export function isUserInRangeOfVenue(
  location: { lat: number; lon: number },
  venue: { lat: number; lon: number; distance: number },
) {
  const distance = getDistanceInMeters(location, venue);

  return distance < venue.distance;
}

export function getDistanceInMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
) {
  const RADIUS_OF_EARTH_IN_KM = 6371; // Radius of the earth in km
  const distanceLatitude = deg2rad(b.lat - a.lat);
  const distanceLongitude = deg2rad(b.lon - a.lon);
  const distance =
    Math.sin(distanceLatitude / 2) * Math.sin(distanceLatitude / 2) +
    Math.cos(deg2rad(a.lat)) *
      Math.cos(deg2rad(b.lat)) *
      Math.sin(distanceLongitude / 2) *
      Math.sin(distanceLongitude / 2);
  const c = 2 * Math.atan2(Math.sqrt(distance), Math.sqrt(1 - distance));
  const d = RADIUS_OF_EARTH_IN_KM * c; // Distance in km
  return d * 1000;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

export function sessionsOverlap(
  a: Pick<Session, "startTime" | "endTime">,
  b: Pick<Session, "startTime" | "endTime">,
) {
  const aStart = a.startTime.toMillis();
  const aEnd = a.endTime?.toMillis() ?? Infinity;
  const bStart = b.startTime.toMillis();
  const bEnd = b.endTime?.toMillis() ?? Infinity;

  return aStart < bEnd && aEnd > bStart;
}

/**
 * Other people's sessions that were active at some point during `session`, sorted by start time.
 * Excludes `session` itself and any other sessions belonging to the same profile.
 */
export function findConcurrentSessions(session: Session, candidates: Session[]) {
  return candidates
    .filter((other) => other.id !== session.id && other.profileId !== session.profileId)
    .filter((other) => sessionsOverlap(session, other))
    .sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
}

/**
 * The session as it earns: one that wasn't a Thursday check-in earns the
 * basic rate, whatever its streak or pub. See Session.thursday.
 */
export function withEarningRate<T extends Pick<Session, "coinRate" | "thursday">>(session: T): T {
  return isThursdayCheckIn(session) ? session : { ...session, coinRate: BASE_COINS_PER_HOUR };
}

/** The hourly coin a session has earned, power-ups included (bonuses aren't). */
export function calculateBasicEarnings(session: Session) {
  const endTime = session.endTime?.toDate() || new Date();

  return calculatePowerupEarnings(
    withEarningRate(session),
    differenceInSeconds(endTime, session.startTime.toDate()),
  );
}

export function calculateCoinEarnings(session: Session) {
  return calculateBasicEarnings(session);
}

/**
 * Everything a session has earned: the hourly coin plus any bonuses (see
 * economy.ts), less whatever its curses took (see curses.ts); milestones
 * that came up in jail are withheld, and a Leech's share is paid to whoever
 * cast it rather than the player. For a live session this is what it's
 * earned so far; pass `curses` to use ones not yet recorded on the session.
 */
export function calculateEarningsBreakdown(
  session: Session,
  now: Date = new Date(),
  curses = session.curses ?? [],
): { hourly: number; bonuses: CoinLineItem[]; siphoned: Siphon[]; total: number } {
  const cursed = affectsEarnings(curses)
    ? getCursedEarnings(withEarningRate(session), curses, now)
    : { hourly: calculateBasicEarnings(session), siphoned: [] };
  const hourly = Math.floor(cursed.hourly);
  const milestoneTimes = getMilestoneTimes(session);
  const bonuses = getSessionBonuses(session, now).filter((bonus) => {
    const at = milestoneTimes.get(bonus.id);

    return !at || !isJailedAt(curses, at);
  });

  return { hourly, bonuses, siphoned: cursed.siphoned, total: hourly + sumCoin(bonuses) };
}

export function calculateEarnings(session: Session | null) {
  if (!session || !session.endTime) {
    return { time: 0, coin: 0 };
  }

  const duration = differenceInMilliseconds(session.endTime.toDate(), session.startTime.toDate());

  return {
    time: duration,
    coin: calculateEarningsBreakdown(session).total,
  };
}

export function calculateAdjustmentNeeded(
  initialSession: Session | null,
  updatedSession: Session | null,
) {
  const initialEarnings = calculateEarnings(initialSession);
  const updatedEarnings = calculateEarnings(updatedSession);

  const timeAdjustment = updatedEarnings.time - initialEarnings.time;
  const coinAdjustment = updatedEarnings.coin - initialEarnings.coin;

  return {
    time: timeAdjustment,
    coin: coinAdjustment,
  };
}
export function getConsecutivePubThursdayAttendance(
  times: Record<string, number> = {},
  onDate: Date = getZonedDate(),
  excusedWeeks: Record<string, boolean> = {},
) {
  return getStreakWeeks(times, onDate, excusedWeeks);
}

export function getPubcoinMultiplier(
  times: Record<string, number> = {},
  onDate: Date = getZonedDate(),
  excusedWeeks: Record<string, boolean> = {},
) {
  return getStreakMultiplier(getConsecutivePubThursdayAttendance(times, onDate, excusedWeeks));
}
